// PWA install state machine.
//
// Three platforms, three different install paths:
//
//   - Android Chrome / Edge: fires `beforeinstallprompt`, app calls
//     `prompt.prompt()` to show the OS install dialog. We capture the
//     event and trigger it from a custom button.
//
//   - iOS Safari: NEVER fires `beforeinstallprompt`. The user has to tap
//     Share → "Zum Home-Bildschirm" manually. We detect iOS Safari and
//     show an instruction sheet instead of an install button. (Push
//     notifications on iOS only work AFTER the app is installed via this
//     route, so the prompt is doubly important on iOS.)
//
//   - Already installed: `display-mode: standalone` matches OR
//     `navigator.standalone` is true (iOS-only legacy property). We hide
//     the install UI entirely.

import { useCallback, useEffect, useState } from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export type Platform = "ios-safari" | "android-chrome" | "desktop" | "other";

export interface PWAInstallHook {
  // Whether we have anything to show. False if already installed, false on
  // unsupported browsers (e.g. desktop Firefox without PWA support).
  canPrompt: boolean;
  // Has the SPA been launched from a home-screen icon?
  isStandalone: boolean;
  // Detected platform — used to switch the UI between native prompt vs
  // iOS instruction sheet.
  platform: Platform;
  // Trigger the native prompt (Android/Chrome). On iOS this is a no-op;
  // the UI shows the instruction sheet instead.
  install: () => Promise<"accepted" | "dismissed" | "ios-manual">;
  // The user has explicitly dismissed the banner. Persisted to localStorage
  // so we don't badger them on every page load.
  dismissed: boolean;
  dismiss: () => void;
}

const DISMISSED_KEY = "pwa_install_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 2 weeks

function detectPlatform(): Platform {
  if (typeof window === "undefined") return "other";
  const ua = navigator.userAgent;
  // iOS check excludes "iPad on iPadOS 13+" which reports as Mac; cover that
  // by also looking for touch + iPad-shaped screen.
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  // Safari check (excludes Chrome/Firefox on iOS, which use WKWebView but
  // have CriOS/FxiOS in UA).
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  if (isIOS && isSafari) return "ios-safari";

  const isAndroid = /Android/i.test(ua);
  const isChromeLike = /Chrome|Edg|OPR|SamsungBrowser/i.test(ua);
  if (isAndroid && isChromeLike) return "android-chrome";

  return /Mobi/i.test(ua) ? "other" : "desktop";
}

function isDismissed(): boolean {
  try {
    const ts = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export function usePWAInstall(): PWAInstallHook {
  const [platform] = useState<Platform>(() => detectPlatform());
  const [isStandalone, setIsStandalone] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const mq = window.matchMedia?.("(display-mode: standalone)").matches ?? false;
    const iosStandalone = (navigator as any).standalone === true;
    return mq || iosStandalone;
  });
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => isDismissed());

  useEffect(() => {
    function onBefore(e: Event) {
      // Stop the browser from showing its own mini-bar. We'll trigger the
      // prompt from our own UI when the user clicks Install.
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }
    function onInstalled() {
      setDeferredPrompt(null);
      setIsStandalone(true);
    }
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async (): Promise<"accepted" | "dismissed" | "ios-manual"> => {
    if (platform === "ios-safari") return "ios-manual";
    if (!deferredPrompt) return "dismissed";
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === "accepted") {
      setDeferredPrompt(null);
    }
    return choice.outcome;
  }, [deferredPrompt, platform]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      /* private mode — no-op */
    }
    setDismissed(true);
  }, []);

  // canPrompt = "we have something to show":
  //   - Already installed → no
  //   - Already dismissed → no (cools down 2 weeks)
  //   - iOS Safari that hasn't been added to home screen → yes (show instructions)
  //   - Android Chrome with a captured prompt → yes
  //   - Desktop Chrome with a captured prompt → yes
  //   - Anything else → no (Firefox iOS, etc.)
  const canPrompt =
    !isStandalone &&
    !dismissed &&
    (platform === "ios-safari" || deferredPrompt !== null);

  return { canPrompt, isStandalone, platform, install, dismiss, dismissed };
}
